import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpIcon from '@mui/icons-material/Help';

const qaData = [
  {
    category: 'Tenant Rights',
    icon: <GavelIcon />,
    questions: [
      {
        question: 'What should I look for in a lease agreement?',
        answer: 'When reviewing a lease agreement, pay attention to: rent amount and due date, security deposit terms, lease duration, pet policies, maintenance responsibilities, subletting rules, and early termination clauses. Always read the fine print and ask questions about anything unclear.'
      },
      {
        question: 'What are my rights regarding security deposits?',
        answer: 'Security deposits are typically limited to 1-2 months rent. Landlords must return deposits within 21-30 days after move-out, with itemized deductions for damages beyond normal wear and tear. You have the right to dispute unreasonable deductions.'
      },
      {
        question: 'What are landlord responsibilities for maintenance and repairs?',
        answer: 'Landlords are responsible for maintaining habitable conditions including: working plumbing, heating, electrical systems, structural integrity, and pest control. Tenants should report issues promptly in writing and keep records of all communications.'
      },
      {
        question: 'Can my landlord enter my apartment without notice?',
        answer: 'Generally, landlords must provide 24-48 hours notice before entering, except in emergencies. Entry should be during reasonable hours and for legitimate purposes like repairs or inspections. Check your state laws for specific requirements.'
      },
      {
        question: 'What protections do I have against eviction?',
        answer: 'Eviction requires proper notice and court proceedings. You cannot be evicted for retaliation, discrimination, or without cause (in rent-controlled areas). If facing eviction, seek legal assistance immediately and respond to all court notices.'
      }
    ]
  },
  {
    category: 'Small tips for international students',
    icon: <DescriptionIcon />,
    questions: [
      {
        question: 'Where can I find a standard lease agreement template?',
        answer: 'You can find lease templates from legal aid organizations, state housing agencies, or reputable legal websites. Always have a lawyer review any legal document before signing. Many universities also provide sample leases for students.'
      },
      {
        question: 'How do I create a roommate agreement?',
        answer: 'A roommate agreement should cover: rent and utility splits, cleaning schedules, guest policies, noise levels, shared expenses, and conflict resolution. Put everything in writing and have all roommates sign. Consider using online templates as a starting point.'
      },
      {
        question: 'What should I include in a notice of intent to vacate?',
        answer: 'Include: your name and address, landlord name and address, date of notice, intended move-out date, forwarding address for deposit return, and any special circumstances. Send via certified mail and keep copies. Follow your lease terms for notice period.'
      },
      {
        question: 'How do I document property condition when moving in?',
        answer: 'Take detailed photos/videos of all rooms, note existing damage, and complete a condition report with your landlord. Document everything in writing with timestamps. This protects your security deposit when moving out.'
      },
      {
        question: 'What\'s the proper way to submit a maintenance request?',
        answer: 'Submit requests in writing (email or letter) with specific details about the issue, include photos if possible, and keep copies. Follow up if repairs aren\'t completed promptly. For emergencies, call immediately and follow up in writing.'
      }
    ]
  },
  {
    category: 'FAQ & Guidelines',
    icon: <HelpIcon />,
    questions: [
      {
        question: 'How much can my rent be increased?',
        answer: 'Rent increase limits vary by location. Some cities have rent control laws limiting increases to 3-5% annually. In uncontrolled areas, increases must be reasonable and not discriminatory. Check your local laws and give proper notice (usually 30-60 days).'
      },
      {
        question: 'What are the rules about pets in rental properties?',
        answer: 'Pet policies vary by landlord. Some allow pets with deposits, others prohibit them entirely. Service animals are protected by law and cannot be denied. Emotional support animals may also be protected. Always get pet policies in writing before signing a lease.'
      },
      {
        question: 'What should I do if I have a dispute with my landlord?',
        answer: 'First, try to resolve issues through direct communication. Document everything in writing. If that fails, contact your local tenant rights organization or legal aid. For serious issues like illegal eviction or habitability problems, seek legal assistance immediately.'
      },
      {
        question: 'What\'s included in a proper move-out checklist?',
        answer: 'Clean thoroughly, repair any damage, return all keys, provide forwarding address, take final photos, complete walk-through with landlord if possible, and keep all receipts and documentation. Give proper notice and follow lease terms exactly.'
      }
    ]
  }
];

function LegalResources() {
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Legal Resources
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Find answers to common rental questions and access legal resources to protect your rights as a tenant or landlord.
      </Typography>

      {/* Q&A Categories */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {qaData.map((category) => (
            <Card key={category.category} sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                {/* Category Header - Always Visible */}
                <Box sx={{ 
                  p: 3, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  borderRadius: '8px 8px 0 0',
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {category.icon}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                      {category.category}
                    </Typography>
                  </Box>
                  <Chip 
                    label={category.questions.length} 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>

                {/* Questions - Each question is collapsible */}
                {category.questions.map((qa, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      borderBottom: index < category.questions.length - 1 ? '1px solid #e0e0e0' : 'none',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 3,
                        py: 2,
                        '&:hover': {
                          bgcolor: 'grey.50',
                        }
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {qa.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {qa.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          ))}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 24 }}>
            <Typography variant="h6" gutterBottom>
              Quick Tips
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Always Document
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Keep copies of all communications, photos of property condition, and receipts for payments.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Know Your Rights
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Familiarize yourself with local tenant laws and your lease terms before signing.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Seek Legal Help
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Contact legal aid organizations for complex issues or if you feel your rights are being violated.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default LegalResources; 